import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { debounce } from 'lodash';

gsap.registerPlugin(ScrollTrigger);

export interface Stack {
    el: HTMLElement;
    stacks: HTMLElement[];
    offsets: number[];
    tween: gsap.core.Tween;
    _progress: number;
    end?: string;
}

const header = document.querySelector<HTMLElement>('.js-header');

export class Stack {
    static instances: any = [];

    static handleResize = debounce(() => {
        this.checkAndScroll();
        ScrollTrigger.refresh();
        Stack.instances.forEach((instance) => instance.refresh());
    }, 100);

    constructor(el: HTMLElement, end?: string) {
        this.el = el;
        this.end = end;
        this.stacks = Array.from(el.querySelectorAll<HTMLElement>('.js-stack'));
        this.offsets = this.stacks.map((element) => element.offsetTop);
        this._progress = 0;

        this.initScrollTrigger();

        Stack.instances.push(this);
        if (Stack.instances.length === 1) {
            window.addEventListener('resize', Stack.handleResize);
        }
    }

    private initScrollTrigger() {
        this.tween = gsap.to(this.el, {
            scrollTrigger: {
                trigger: this.el,
                start: () => `top top+=${header?.getBoundingClientRect().height || 0}`,
                end: () => this.end || `+=${this.stacks.reduce((acc, element) => acc + element.offsetHeight, 0)}`,
                pin: this.shouldPin(),
                pinSpacing: false,
                anticipatePin: 1,
                onUpdate: ({ progress }) => {
                    this._progress = progress;
                    this.update(progress);
                },
                onRefresh: () => this.handleRefresh(),
            },
        });
    }

    private shouldPin(): boolean {
        return matchMedia('(max-width: 1024px)').matches || this.el.dataset.stacksPin === 'true';
    }

    private handleRefresh() {
        for (let i = 1; i < this.stacks.length; i++) {
            this.stacks[i].style.transform = '';
        }
        this.offsets = this.stacks.map((element) => element.offsetTop);
        this.update(this._progress);
    }

    private static checkAndScroll() {
        const firstSection = document.querySelector<HTMLDivElement>('.js-stacks-scroll-section');
        if (!firstSection) return;

        const rect = firstSection.getBoundingClientRect();

        window.scrollTo(0, rect.bottom + rect.height);
    }

    update(progress: number) {
        for (let i = 1; i < this.stacks.length; i++) {
            const translateLimit = this.offsets[i] - 30 * i;
            this.stacks[i].style.transform = `translate3d(0, ${Math.max(
                -translateLimit,
                progress * (1 / i) * (this.offsets[i - 1] - this.stacks.length * translateLimit),
            )}px, 0)`;
        }
    }

    refresh() {
        this.tween?.scrollTrigger?.kill();
        this.tween?.kill();
        this.stacks = Array.from(this.el.querySelectorAll<HTMLElement>('.js-stack'));
        this.offsets = this.stacks.map((element) => element.offsetTop);
        this.initScrollTrigger();
    }

    destroy() {
        this.tween?.scrollTrigger?.kill();
        this.tween?.kill();
        const index = Stack.instances.indexOf(this);
        if (index !== -1) Stack.instances.splice(index, 1);
        if (Stack.instances.length === 0) {
            window.removeEventListener('resize', Stack.handleResize);
        }
    }
}
